// ===================================
// START SESSION PAGE - IMPROVED UI
// src/pages/StartSession.js
// Cleaner validation and start interview flow
// ===================================

import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { validateAccessCode } from '../services/sessionsApi';
import { getPhoneNumber } from '../services/configApi';
import './StartSession.css';

const StartSession = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [code, setCode] = useState(searchParams.get('code') || '');
  const [session, setSession] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setCode(codeFromUrl);
      // Auto-validate if code is in URL
      handleValidate(codeFromUrl);
    }
  }, []);

  const handleCodeChange = (e) => {
    const newCode = e.target.value.toUpperCase();
    setCode(newCode);
    setError('');
    // Clear session if code changes significantly
    if (session && newCode !== code) {
      setSession(null);
    }
  };

  const handleValidate = async (accessCode) => {
    const codeToValidate = accessCode || code;
    if (!codeToValidate || codeToValidate.length !== 8) {
      setError('Please enter a valid 8-character access code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const sessionData = await validateAccessCode(codeToValidate);
      
      if (!sessionData || !sessionData.id) {
        setError('Invalid session data received. Please try again.');
        setSession(null);
        return;
      }
      
      setSession(sessionData);
if (sessionData.interview_type === 'phone') {
  try {
    const phoneRes = await getPhoneNumber(codeToValidate);
    // Axios wraps response in .data property
    const phoneData = phoneRes.data || phoneRes;
    const number = phoneData.phoneNumber || '';
    console.log('📞 Phone number received:', number);
    setPhoneNumber(number);
  } catch (phoneErr) {
    console.log('Could not get phone number:', phoneErr.message);
    // Use env fallback if API fails
    setPhoneNumber(process.env.REACT_APP_PHONE_NUMBER || '+1 (555) 123-4567');
  }
}
    } catch (err) {
      console.error('Validation error:', err);
      setError(err.response?.data?.message || err.message || 'Invalid access code');
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStartInterview = () => {
    if (session && session.id) {
      navigate(`/app/interview/${session.id}`);
    } else {
      setError('No valid session found. Please validate your code again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && code.length === 8 && !session) {
      handleValidate();
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'voice': return '🎙️';
      case 'phone': return '📞';
      default: return '💬';
    }
  };

  // Inline styles for the improved layout
  const styles = {
    page: {
      minHeight: 'calc(100vh - 100px)',
      padding: '2rem'
    },
    container: {
      maxWidth: '900px',
      margin: '0 auto'
    },
    header: {
      textAlign: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontFamily: "'Playfair Display', serif",
      fontSize: '2.5rem',
      color: '#fff',
      margin: '0 0 0.5rem'
    },
    subtitle: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: '1.1rem',
      margin: 0
    },
    mainCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '16px',
      padding: '2rem',
      marginBottom: '2rem'
    },
    inputSection: {
      marginBottom: session ? '1.5rem' : 0,
      paddingBottom: session ? '1.5rem' : 0,
      borderBottom: session ? '1px solid rgba(255, 255, 255, 0.08)' : 'none'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      color: 'rgba(255, 255, 255, 0.6)',
      marginBottom: '0.5rem',
      fontWeight: '500'
    },
    inputRow: {
      display: 'flex',
      gap: '1rem',
      alignItems: 'stretch'
    },
    input: {
      flex: 1,
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '10px',
      padding: '1rem 1.25rem',
      fontSize: '1.25rem',
      color: '#fff',
      fontFamily: "'SF Mono', monospace",
      letterSpacing: '0.15em',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s'
    },
    validateBtn: {
      padding: '1rem 2rem',
      background: session ? 'rgba(34, 197, 94, 0.2)' : 'linear-gradient(135deg, #d4af37, #b8962e)',
      color: session ? '#22c55e' : '#000',
      border: session ? '1px solid rgba(34, 197, 94, 0.3)' : 'none',
      borderRadius: '10px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: session ? 'default' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      whiteSpace: 'nowrap',
      transition: 'all 0.2s'
    },
    error: {
      marginTop: '1rem',
      padding: '0.875rem 1rem',
      background: 'rgba(239, 68, 68, 0.1)',
      border: '1px solid rgba(239, 68, 68, 0.3)',
      borderRadius: '8px',
      color: '#f87171',
      fontSize: '0.9rem'
    },
    // Session Details
    sessionSection: {
      animation: 'fadeIn 0.3s ease'
    },
    sessionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem'
    },
    sessionIcon: {
      width: '60px',
      height: '60px',
      background: 'rgba(212, 175, 55, 0.15)',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.75rem'
    },
    sessionTitle: {
      flex: 1
    },
    sessionName: {
      fontFamily: "'Playfair Display', serif",
      fontSize: '1.5rem',
      color: '#fff',
      margin: '0 0 0.25rem',
      textTransform: 'capitalize'
    },
    sessionCategory: {
      color: 'rgba(255, 255, 255, 0.5)',
      fontSize: '0.9rem',
      margin: 0
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '1rem',
      marginBottom: '1.5rem',
      background: 'rgba(255, 255, 255, 0.02)',
      borderRadius: '10px',
      padding: '1rem'
    },
    infoItem: {
      textAlign: 'center'
    },
    infoLabel: {
      display: 'block',
      fontSize: '0.75rem',
      color: 'rgba(255, 255, 255, 0.4)',
      marginBottom: '0.25rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    infoValue: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#fff',
      textTransform: 'capitalize'
    },
    readyBadge: {
      display: 'inline-block',
      padding: '0.25rem 0.75rem',
      background: 'rgba(34, 197, 94, 0.2)',
      color: '#22c55e',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: '600',
      textTransform: 'uppercase'
    },
    startBtn: {
      width: '100%',
      padding: '1.25rem 2rem',
      background: 'linear-gradient(135deg, #d4af37, #b8962e)',
      color: '#000',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1.2rem',
      fontWeight: '700',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.75rem',
      boxShadow: '0 4px 20px rgba(212, 175, 55, 0.3)',
      transition: 'all 0.2s'
    },
    // Phone section
    phoneSection: {
      background: 'rgba(59, 130, 246, 0.1)',
      border: '1px solid rgba(59, 130, 246, 0.2)',
      borderRadius: '12px',
      padding: '1.5rem',
      textAlign: 'center'
    },
    phoneNumber: {
      fontFamily: "'SF Mono', monospace",
      fontSize: '2rem',
      fontWeight: '700',
      color: '#60a5fa',
      margin: '1rem 0'
    },
    phoneSteps: {
      textAlign: 'left',
      paddingLeft: '1.5rem',
      margin: '1rem 0 0',
      color: 'rgba(255, 255, 255, 0.7)'
    },
    // Tips sidebar
    sidebar: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '1.5rem'
    },
    tipCard: {
      background: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '12px',
      padding: '1.5rem'
    },
    tipTitle: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#fff',
      margin: '0 0 1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    tipList: {
      margin: 0,
      paddingLeft: '1.25rem',
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.9rem',
      lineHeight: '1.8'
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Start Your Interview</h1>
          <p style={styles.subtitle}>Enter your access code to begin your practice session</p>
        </div>

        {/* Main Card */}
        <div style={styles.mainCard}>
          {/* Code Input Section */}
          <div style={styles.inputSection}>
            <label style={styles.label}>Access Code</label>
            <div style={styles.inputRow}>
              <input
                type="text"
                style={{
                  ...styles.input,
                  borderColor: error ? 'rgba(239, 68, 68, 0.5)' : session ? 'rgba(34, 197, 94, 0.3)' : 'rgba(255, 255, 255, 0.1)'
                }}
                value={code}
                onChange={handleCodeChange}
                onKeyPress={handleKeyPress}
                placeholder="XXXXXXXX"
                maxLength={8}
                autoComplete="off"
                disabled={loading}
              />
              <button
                style={{
                  ...styles.validateBtn,
                  opacity: loading || (session && code.length === 8) ? 0.7 : 1,
                  cursor: loading ? 'wait' : session ? 'default' : code.length === 8 ? 'pointer' : 'not-allowed'
                }}
                onClick={() => !session && handleValidate()}
                disabled={loading || code.length !== 8 || session}
              >
                {loading ? (
                  <>
                    <span className="spinner-small"></span>
                    Validating...
                  </>
                ) : session ? (
                  <>✓ Validated</>
                ) : (
                  'Validate'
                )}
              </button>
            </div>
            
            {error && <div style={styles.error}>{error}</div>}
          </div>

          {/* Session Details - Shows after validation */}
          {session && (
            <div style={styles.sessionSection}>
              {/* Session Header */}
              <div style={styles.sessionHeader}>
                <div style={styles.sessionIcon}>
                  {getTypeIcon(session.interview_type)}
                </div>
                <div style={styles.sessionTitle}>
                  <h2 style={styles.sessionName}>{session.interviewer_personality} Interview</h2>
                  {(session.category_name || session.subcategory_name) && (
                    <p style={styles.sessionCategory}>
                      {session.category_name}
                      {session.subcategory_name && ` • ${session.subcategory_name}`}
                    </p>
                  )}
                </div>
              </div>

              {/* Info Grid */}
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Type</span>
                  <span style={styles.infoValue}>{session.interview_type}</span>
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Duration</span>
                  <span style={styles.infoValue}>{session.duration} min</span>
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Level</span>
                  <span style={styles.infoValue}>{session.experience_level}</span>
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Status</span>
                  <span style={styles.readyBadge}>Ready</span>
                </div>
              </div>

              {/* Company Name */}
              {session.company_name && (
                <div style={{ marginBottom: '1.5rem', color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.9rem' }}>
                  🏢 Target Company: <strong style={{ color: '#fff' }}>{session.company_name}</strong>
                </div>
              )}

              {/* Realtime Feedback */}
              {session.realtime_feedback && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <span style={{ 
                    display: 'inline-block',
                    padding: '0.375rem 0.875rem',
                    background: 'rgba(251, 191, 36, 0.15)',
                    color: '#fbbf24',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '500'
                  }}>
                    💡 Realtime Feedback Enabled
                  </span>
                </div>
              )}

              {/* Start Button or Phone Section */}
              {session.interview_type === 'phone' ? (
                <div style={styles.phoneSection}>
                  <h3 style={{ color: '#fff', margin: '0 0 0.5rem' }}>📞 Call to Start</h3>
                  <div style={styles.phoneNumber}>{phoneNumber || 'Loading...'}</div>
                  <ol style={styles.phoneSteps}>
                    <li>Call the number above</li>
                    <li>Enter your code: <code style={{ color: '#d4af37' }}>{code}</code></li>
                    <li>Your interview will begin</li>
                  </ol>
                </div>
              ) : (
                <button 
                  style={styles.startBtn}
                  onClick={handleStartInterview}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(212, 175, 55, 0.4)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(212, 175, 55, 0.3)';
                  }}
                >
                  🚀 Start Interview
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tips Section */}
        <div style={styles.sidebar}>
          <div style={styles.tipCard}>
            <h3 style={styles.tipTitle}>💡 Tips for Success</h3>
            <ul style={styles.tipList}>
              <li>Find a quiet space with no distractions</li>
              <li>Have a glass of water nearby</li>
              <li>Speak clearly and take your time</li>
              <li>Use specific examples in your answers</li>
              <li>Remember the STAR method for behavioral questions</li>
            </ul>
          </div>

          <div style={styles.tipCard}>
            <h3 style={styles.tipTitle}>❓ Need Help?</h3>
            <ul style={styles.tipList}>
              <li>Make sure you've completed payment</li>
              <li>Check your email for the code</li>
              <li>Codes are 8 characters (letters/numbers)</li>
              <li>Each code can only be used once</li>
              <li>Contact support if issues persist</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Keyframe animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .spinner-small {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(0, 0, 0, 0.2);
          border-top-color: #000;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default StartSession;